import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import projectsService from '../services/projectsService';
import type { CompletionFeedbackSourceDocument, ProjectCompletionFeedbackPayload } from '../types';

type RefAuth = '' | 'yes_unrestricted' | 'yes_prior_approval' | 'no';

interface FormState {
  certificate_date: string;
  project_name_reference: string;
  client_company: string;
  dear_name: string;
  project_duration_start: string;
  project_duration_end: string;
  warranty_end_text: string;
  nd_signatory_name: string;
  nd_signatory_title: string;
  client_agreement_company: string;
  client_signatory_name: string;
  client_signatory_title: string;
  overall_satisfaction: string;
  technical_quality: string;
  communication_responsiveness: string;
  schedule_timeliness: string;
  understanding_requirements: string;
  optional_comments: string;
  reference_authorization: RefAuth;
  respondent_name: string;
  respondent_company: string;
  respondent_position: string;
  respondent_signature_date: string;
}

const emptyForm = (): FormState => ({
  certificate_date: '',
  project_name_reference: '',
  client_company: '',
  dear_name: '',
  project_duration_start: '',
  project_duration_end: '',
  warranty_end_text: '',
  nd_signatory_name: '',
  nd_signatory_title: '',
  client_agreement_company: '',
  client_signatory_name: '',
  client_signatory_title: '',
  overall_satisfaction: '',
  technical_quality: '',
  communication_responsiveness: '',
  schedule_timeliness: '',
  understanding_requirements: '',
  optional_comments: '',
  reference_authorization: '',
  respondent_name: '',
  respondent_company: '',
  respondent_position: '',
  respondent_signature_date: '',
});

function payloadToForm(p: ProjectCompletionFeedbackPayload): FormState {
  const s = (v: string | null | undefined) => v ?? '';
  const r = (v: number | null | undefined) =>
    v != null && v >= 1 && v <= 4 ? String(v) : '';
  return {
    certificate_date: s(p.certificate_date),
    project_name_reference: s(p.project_name_reference),
    client_company: s(p.client_company),
    dear_name: s(p.dear_name),
    project_duration_start: s(p.project_duration_start),
    project_duration_end: s(p.project_duration_end),
    warranty_end_text: s(p.warranty_end_text),
    nd_signatory_name: s(p.nd_signatory_name),
    nd_signatory_title: s(p.nd_signatory_title),
    client_agreement_company: s(p.client_agreement_company),
    client_signatory_name: s(p.client_signatory_name),
    client_signatory_title: s(p.client_signatory_title),
    overall_satisfaction: r(p.overall_satisfaction),
    technical_quality: r(p.technical_quality),
    communication_responsiveness: r(p.communication_responsiveness),
    schedule_timeliness: r(p.schedule_timeliness),
    understanding_requirements: r(p.understanding_requirements),
    optional_comments: s(p.optional_comments),
    reference_authorization: (p.reference_authorization ?? '') as RefAuth,
    respondent_name: s(p.respondent_name),
    respondent_company: s(p.respondent_company),
    respondent_position: s(p.respondent_position),
    respondent_signature_date: s(p.respondent_signature_date),
  };
}

function formToPayload(f: FormState): ProjectCompletionFeedbackPayload {
  const num = (s: string) => (s === '' ? null : Number(s));
  return {
    certificate_date: f.certificate_date || null,
    project_name_reference: f.project_name_reference || null,
    client_company: f.client_company || null,
    dear_name: f.dear_name || null,
    project_duration_start: f.project_duration_start || null,
    project_duration_end: f.project_duration_end || null,
    warranty_end_text: f.warranty_end_text || null,
    nd_signatory_name: f.nd_signatory_name || null,
    nd_signatory_title: f.nd_signatory_title || null,
    client_agreement_company: f.client_agreement_company || null,
    client_signatory_name: f.client_signatory_name || null,
    client_signatory_title: f.client_signatory_title || null,
    overall_satisfaction: num(f.overall_satisfaction),
    technical_quality: num(f.technical_quality),
    communication_responsiveness: num(f.communication_responsiveness),
    schedule_timeliness: num(f.schedule_timeliness),
    understanding_requirements: num(f.understanding_requirements),
    optional_comments: f.optional_comments || null,
    reference_authorization: f.reference_authorization === '' ? null : f.reference_authorization,
    respondent_name: f.respondent_name || null,
    respondent_company: f.respondent_company || null,
    respondent_position: f.respondent_position || null,
    respondent_signature_date: f.respondent_signature_date || null,
  };
}

const REF_AUTH_LABELS: Record<string, string> = {
  yes_unrestricted: 'Yes, without restriction',
  yes_prior_approval: 'Yes, subject to prior approval',
  no: 'No',
};

const RATING_LABELS: Record<string, string> = {
  '4': '4 — Above expectations',
  '3': '3 — Good',
  '2': '2 — Good',
  '1': '1 — Below expectations',
};

function RatingRow({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
}) {
  if (readOnly) {
    return (
      <Grid item xs={12} sm={6}>
        <TextField
          label={label}
          value={value ? (RATING_LABELS[value] ?? value) : '—'}
          fullWidth
          size="small"
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    );
  }
  return (
    <Grid item xs={12}>
      <FormControl component="fieldset" variant="standard" fullWidth>
        <FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}>
          {label}
        </FormLabel>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          4 = above expectations · 3 / 2 = good · 1 = below expectations
        </Typography>
        <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
          <FormControlLabel value="" control={<Radio size="small" />} label="—" />
          {[1, 2, 3, 4].map((n) => (
            <FormControlLabel key={n} value={String(n)} control={<Radio size="small" />} label={String(n)} />
          ))}
        </RadioGroup>
      </FormControl>
    </Grid>
  );
}

interface FeedbackItem {
  id: number;
  submitted_by_user_id: number;
  submitted_by_name: string;
  payload: ProjectCompletionFeedbackPayload;
  created_at: string | null;
  source_document?: CompletionFeedbackSourceDocument | null;
}

interface Props {
  projectId: number;
  onFeedbackCountChange?: (count: number) => void;
}

const NAUTILUS = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
};

const ProjectCompletionFeedbackPanel: React.FC<Props> = ({ projectId, onFeedbackCountChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingSource, setDownloadingSource] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadFeedback = useCallback(async () => {
    try {
      const data = await projectsService.listCompletionFeedback(projectId, 50);
      setFeedbackItems(data.items);
      onFeedbackCountChange?.(data.items.length);
      if (data.items.length > 0) {
        setForm(payloadToForm(data.items[0].payload));
        setSelectedIdx(0);
      }
    } catch {
      setFeedbackItems([]);
      onFeedbackCountChange?.(0);
    }
  }, [projectId, onFeedbackCountChange]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleSelectFeedback = (idx: number) => {
    if (editing) return;
    setSelectedIdx(idx);
    setForm(payloadToForm(feedbackItems[idx].payload));
    setError(null);
    setSuccess(null);
  };

  const handleEdit = () => {
    setEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditing(false);
    setForm(payloadToForm(feedbackItems[selectedIdx].payload));
    setError(null);
  };

  const handleSave = async () => {
    const item = feedbackItems[selectedIdx];
    if (!item) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await projectsService.updateCompletionFeedback(projectId, item.id, formToPayload(form));
      setSuccess('Feedback updated.');
      setEditing(false);
      await loadFeedback();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setError(msg || 'Failed to update feedback.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSourceDocument = async () => {
    const item = feedbackItems[selectedIdx];
    if (!item?.source_document) return;
    setDownloadingSource(true);
    setError(null);
    try {
      await projectsService.downloadCompletionFeedbackSourceDocument(projectId, item.id, item.source_document.filename);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message || '')
          : '';
      setError(msg || 'Failed to download source document.');
    } finally {
      setDownloadingSource(false);
    }
  };

  if (feedbackItems.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="text.secondary">No feedback submitted yet.</Typography>
      </Box>
    );
  }

  const current = feedbackItems[selectedIdx];
  const ro = !editing;

  const fieldProps = (label: string, key: keyof FormState, opts?: { multiline?: boolean; type?: string }) => ({
    label,
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value }),
    fullWidth: true,
    size: 'small' as const,
    InputProps: { readOnly: ro },
    InputLabelProps: { shrink: true },
    ...(opts?.multiline ? { multiline: true, minRows: 2 } : {}),
    ...(opts?.type ? { type: opts.type } : {}),
  });

  return (
    <Box sx={{ height: '100%', overflow: 'auto', px: 2, py: 2 }}>
      {feedbackItems.length > 1 && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {feedbackItems.map((item, idx) => (
            <Chip
              key={item.id}
              label={`#${item.id} · ${item.submitted_by_name}${item.created_at ? ` · ${new Date(item.created_at).toLocaleDateString()}` : ''}`}
              variant={idx === selectedIdx ? 'filled' : 'outlined'}
              color={idx === selectedIdx ? 'primary' : 'default'}
              onClick={() => handleSelectFeedback(idx)}
              size="small"
            />
          ))}
        </Box>
      )}

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Submitted by {current.submitted_by_name}
            {current.created_at ? ` on ${new Date(current.created_at).toLocaleString()}` : ''}
          </Typography>
          {current.source_document?.filename && (
            <Typography variant="caption" color="text.secondary" display="block">
              Source document: {current.source_document.filename}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1}>
          {current.source_document?.filename && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSourceDocument}
              disabled={downloadingSource}
            >
              {downloadingSource ? 'Downloading…' : 'Source doc'}
            </Button>
          )}
          {!editing ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{
                bgcolor: NAUTILUS.sand,
                color: NAUTILUS.deepOcean,
                fontWeight: 600,
                '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85) },
              }}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  bgcolor: NAUTILUS.sand,
                  color: NAUTILUS.deepOcean,
                  fontWeight: 600,
                  '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85) },
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Letter header
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Certificate date', 'certificate_date', { type: ro ? undefined : 'date' })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Project (name / reference)', 'project_name_reference')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Client company', 'client_company')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Dear (name)', 'dear_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Project duration — start', 'project_duration_start', { type: ro ? undefined : 'date' })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Project duration — completion', 'project_duration_end', { type: ro ? undefined : 'date' })} />
        </Grid>
        <Grid item xs={12}>
          <TextField {...fieldProps('Warranty / completion notes', 'warranty_end_text', { multiline: true })} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Signatories (letter)
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Nautilus Dynamics — name', 'nd_signatory_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Nautilus Dynamics — title', 'nd_signatory_title')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Client agreement — company', 'client_agreement_company')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Client — signatory name', 'client_signatory_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Client — signatory title', 'client_signatory_title')} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Client feedback &amp; satisfaction
      </Typography>
      <Grid container spacing={2}>
        <RatingRow
          label="Overall satisfaction"
          value={form.overall_satisfaction}
          onChange={(v) => setForm({ ...form, overall_satisfaction: v })}
          readOnly={ro}
        />
        <RatingRow
          label="Technical quality of deliverables"
          value={form.technical_quality}
          onChange={(v) => setForm({ ...form, technical_quality: v })}
          readOnly={ro}
        />
        <RatingRow
          label="Communication & responsiveness"
          value={form.communication_responsiveness}
          onChange={(v) => setForm({ ...form, communication_responsiveness: v })}
          readOnly={ro}
        />
        <RatingRow
          label="Schedule & timeliness"
          value={form.schedule_timeliness}
          onChange={(v) => setForm({ ...form, schedule_timeliness: v })}
          readOnly={ro}
        />
        <RatingRow
          label="Understanding of requirements"
          value={form.understanding_requirements}
          onChange={(v) => setForm({ ...form, understanding_requirements: v })}
          readOnly={ro}
        />
        <Grid item xs={12}>
          <TextField {...fieldProps('Optional comments (voice of customer)', 'optional_comments', { multiline: true })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          {ro ? (
            <TextField
              label="Reference authorization"
              value={form.reference_authorization ? (REF_AUTH_LABELS[form.reference_authorization] ?? form.reference_authorization) : '—'}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <TextField
              select
              label="Reference authorization"
              fullWidth
              size="small"
              value={form.reference_authorization}
              onChange={(e) => setForm({ ...form, reference_authorization: e.target.value as RefAuth })}
              helperText="May we include your organization as a reference?"
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              <MenuItem value="yes_unrestricted">Yes, without restriction</MenuItem>
              <MenuItem value="yes_prior_approval">Yes, subject to prior approval</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Respondent (footer)
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Name', 'respondent_name')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Company', 'respondent_company')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Position', 'respondent_position')} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField {...fieldProps('Date', 'respondent_signature_date', { type: ro ? undefined : 'date' })} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectCompletionFeedbackPanel;
